/**
 * Once the conversation has moved on, a guide card (question, confirmation) is history: it folds
 * to a single muted line and opens again on click. The card itself stays untouched inside — the
 * wrapper only decides how prominent it is.
 *
 * `collapsed` is the caller's call (`!isLastMessage` in the chat), so the rule is one place:
 * the card of the newest message is open, every older one is folded. The fold animates: the
 * body is a grid row going 1fr → 0fr (height auto → 0 without measuring), the header line fades
 * in. `motion-reduce` turns both into a cut.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface GuideCollapsedProps {
    collapsed: boolean;
    /** The one line shown while folded. */
    summary: string;
    className?: string;
    children: ReactNode;
}

export function GuideCollapsed({ collapsed, summary, className, children }: GuideCollapsedProps) {
    const [open, setOpen] = useState(!collapsed);
    // The prop decides; a click only overrides until the prop changes again.
    useEffect(() => setOpen(!collapsed), [collapsed]);

    return (
        <div className={cn('w-full', className)} data-testid={collapsed ? 'guide-collapsed' : undefined} data-open={open ? 'true' : 'false'}>
            <div
                className={cn(
                    'grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none',
                    collapsed ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                )}
                aria-hidden={!collapsed}
            >
                <div className='overflow-hidden'>
                    <button
                        type='button'
                        tabIndex={collapsed ? 0 : -1}
                        aria-expanded={open}
                        onClick={() => setOpen((previous) => !previous)}
                        className='my-1 flex w-full items-center gap-1.5 rounded-md border border-border/60 bg-muted/20 px-2.5 py-1.5 text-left text-xs text-muted-foreground hover:bg-muted/40'
                    >
                        <ChevronRight
                            className={cn('size-3.5 shrink-0 transition-transform duration-300 motion-reduce:transition-none', open && 'rotate-90')}
                            aria-hidden
                        />
                        <span className='min-w-0 truncate'>{summary}</span>
                    </button>
                </div>
            </div>
            <div
                className={cn(
                    'grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none',
                    open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                )}
                aria-hidden={!open}
                data-testid='guide-collapsed-body'
            >
                <div className={cn('overflow-hidden', !open && 'pointer-events-none')}>{children}</div>
            </div>
        </div>
    );
}
